import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarIcon, Search, X, Filter } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";

interface ActivityFiltersProps {
  gyms: Array<{ id: string; name: string; city: string }>;
  onFilterChange: (filters: {
    gymIds: string[];
    actions: string[];
    startDate?: Date;
    endDate?: Date;
    search: string;
  }) => void;
}

const ACTION_TYPES = [
  'LOGIN',
  'LOGOUT',
  'BOOKING_CREATED',
  'BOOKING_CANCELLED',
  'BOOKING_FAILED',
  'SUBSCRIPTION_STARTED',
  'SUBSCRIPTION_CANCELLED',
  'SUBSCRIPTION_UPDATED',
  'SUBSCRIPTION_PAUSED',
  'SUBSCRIPTION_RESUMED',
  'PROFILE_UPDATED',
  'CLASS_ATTENDED',
  'CLIENT_CREATED',
  'CLIENT_UPDATED',
  'CLIENT_DELETED',
  'PAYMENT_CREATED',
  'PAYMENT_UPDATED',
  'PAYMENT_DELETED',
  'CLASS_CREATED',
  'CLASS_UPDATED',
  'CLASS_DELETED',
  'COURSE_CREATED',
  'COURSE_UPDATED',
  'COURSE_DELETED',
];

function useActionLabels(): Record<string, string> {
  const { t } = useTranslation("superadmin");
  return {
    'LOGIN': t('actionLabels.login'),
    'LOGOUT': t('actionLabels.logout'),
    'BOOKING_CREATED': t('actionLabels.bookingCreated'),
    'BOOKING_CANCELLED': t('actionLabels.bookingCancelled'),
    'BOOKING_FAILED': t('actionLabels.bookingFailed'),
    'SUBSCRIPTION_STARTED': t('actionLabels.subscriptionStarted'),
    'SUBSCRIPTION_CANCELLED': t('actionLabels.subscriptionCancelled'),
    'SUBSCRIPTION_UPDATED': t('actionLabels.subscriptionUpdated'),
    'SUBSCRIPTION_PAUSED': t('actionLabels.subscriptionPaused'),
    'SUBSCRIPTION_RESUMED': t('actionLabels.subscriptionResumed'),
    'PROFILE_UPDATED': t('actionLabels.profileUpdated'),
    'CLASS_ATTENDED': t('actionLabels.classAttended'),
    'CLIENT_CREATED': t('actionLabels.clientCreated'),
    'CLIENT_UPDATED': t('actionLabels.clientUpdated'),
    'CLIENT_DELETED': t('actionLabels.clientDeleted'),
    'PAYMENT_CREATED': t('actionLabels.paymentCreated'),
    'PAYMENT_UPDATED': t('actionLabels.paymentUpdated'),
    'PAYMENT_DELETED': t('actionLabels.paymentDeleted'),
    'CLASS_CREATED': t('actionLabels.classCreated'),
    'CLASS_UPDATED': t('actionLabels.classUpdated'),
    'CLASS_DELETED': t('actionLabels.classDeleted'),
    'COURSE_CREATED': t('actionLabels.courseCreated'),
    'COURSE_UPDATED': t('actionLabels.courseUpdated'),
    'COURSE_DELETED': t('actionLabels.courseDeleted'),
  };
}

export function ActivityFilters({ gyms, onFilterChange }: ActivityFiltersProps) {
  const { t } = useTranslation("superadmin");
  const ACTION_LABELS = useActionLabels();
  const isMobile = useIsMobile();
  const [selectedGyms, setSelectedGyms] = useState<string[]>([]);
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [search, setSearch] = useState("");
  const [actionsDrawerOpen, setActionsDrawerOpen] = useState(false);

  const applyFilters = () => {
    onFilterChange({
      gymIds: selectedGyms,
      actions: selectedActions,
      startDate,
      endDate,
      search,
    });
  };

  const resetFilters = () => {
    setSelectedGyms([]);
    setSelectedActions([]);
    setStartDate(undefined);
    setEndDate(undefined);
    setSearch("");
    onFilterChange({
      gymIds: [],
      actions: [],
      search: "",
    });
  };

  const toggleGym = (gymId: string) => {
    setSelectedGyms((prev) =>
      prev.includes(gymId) ? prev.filter((id) => id !== gymId) : [...prev, gymId]
    );
  };

  const toggleAction = (action: string) => {
    setSelectedActions((prev) =>
      prev.includes(action) ? prev.filter((a) => a !== action) : [...prev, action]
    );
  };

  const activeFiltersCount =
    selectedGyms.length + selectedActions.length + (startDate ? 1 : 0) + (endDate ? 1 : 0) + (search ? 1 : 0);

  return (
    <div className="space-y-3 md:space-y-4 p-3 md:p-4 bg-white/10 border-white/20 rounded-lg">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4" />
          <h3 className="font-semibold text-sm md:text-base">{t("filters.title")}</h3>
          {activeFiltersCount > 0 && (
            <Badge variant="secondary" className="h-5">
              {activeFiltersCount}
            </Badge>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={resetFilters}>
            {isMobile ? t("filters.reset") : t("filters.resetFull")}
          </Button>
          <Button size="sm" onClick={applyFilters}>
            {t("filters.apply")}
          </Button>
        </div>
      </div>

      {/* Mobile Layout */}
      {isMobile ? (
        <div className="space-y-3">
          {/* Search - Always visible */}
          <div className="space-y-2">
            <Label className="text-xs">{t("filters.search")}</Label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("filters.searchPlaceholderShort")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9"
              />
            </div>
          </div>

          {/* Filters in Accordion */}
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="dates">
              <AccordionTrigger className="py-2">
                <span className="text-sm">{t("filters.dates")} {(startDate || endDate) && `(${(startDate ? 1 : 0) + (endDate ? 1 : 0)})`}</span>
              </AccordionTrigger>
              <AccordionContent className="space-y-3 pt-2">
                <div className="space-y-2">
                  <Label className="text-xs">{t("filters.startDate")}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !startDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startDate ? format(startDate, "dd/MM/yyyy") : t("filters.select")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={setStartDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">{t("filters.endDate")}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !endDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endDate ? format(endDate, "dd/MM/yyyy") : t("filters.select")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="tenants">
              <AccordionTrigger className="py-2">
                <span className="text-sm">{t("filters.tenants")} ({selectedGyms.length})</span>
              </AccordionTrigger>
              <AccordionContent className="space-y-2 pt-2">
                <Select onValueChange={toggleGym}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder={t("filters.selectTenant")} />
                  </SelectTrigger>
                  <SelectContent>
                    {gyms.map((gym) => (
                      <SelectItem key={gym.id} value={gym.id}>
                        {gym.name} - {gym.city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedGyms.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {selectedGyms.map((gymId) => {
                      const gym = gyms.find((g) => g.id === gymId);
                      return (
                        <Badge key={gymId} variant="secondary" className="text-xs">
                          {gym?.name}
                          <X
                            className="h-3 w-3 ml-1 cursor-pointer"
                            onClick={() => toggleGym(gymId)}
                          />
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Actions Drawer */}
          <Drawer open={actionsDrawerOpen} onOpenChange={setActionsDrawerOpen}>
            <DrawerTrigger asChild>
              <Button variant="outline" size="sm" className="w-full justify-between">
                <span>{t("filters.actionTypes")} ({selectedActions.length})</span>
                <Filter className="h-4 w-4" />
              </Button>
            </DrawerTrigger>
            <DrawerContent>
              <DrawerHeader>
                <DrawerTitle>{t("filters.filterByActionType")}</DrawerTitle>
              </DrawerHeader>
              <div className="px-4 pb-6 space-y-3 max-h-[60vh] overflow-y-auto">
                {ACTION_TYPES.map((action) => (
                  <div key={action} className="flex items-center space-x-3">
                    <Checkbox
                      id={action}
                      checked={selectedActions.includes(action)}
                      onCheckedChange={() => toggleAction(action)}
                    />
                    <Label htmlFor={action} className="text-sm flex-1 cursor-pointer">
                      {ACTION_LABELS[action] || action}
                    </Label>
                  </div>
                ))}
              </div>
            </DrawerContent>
          </Drawer>
        </div>
      ) : (
        /* Desktop Layout */
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="space-y-2">
              <Label>{t("filters.search")}</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("filters.searchPlaceholderFull")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <Label>{t("filters.startDate")}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "dd/MM/yyyy") : t("filters.select")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* End Date */}
            <div className="space-y-2">
              <Label>{t("filters.endDate")}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !endDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "dd/MM/yyyy") : t("filters.select")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Gym Filter */}
            <div className="space-y-2">
              <Label>{t("filters.tenants")}</Label>
              <Select onValueChange={toggleGym}>
                <SelectTrigger>
                  <SelectValue placeholder={t("filters.selectedCount", { count: selectedGyms.length })} />
                </SelectTrigger>
                <SelectContent>
                  {gyms.map((gym) => (
                    <SelectItem key={gym.id} value={gym.id}>
                      {gym.name} - {gym.city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Selected Gyms */}
          {selectedGyms.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <Label className="w-full text-sm text-muted-foreground">{t("filters.selectedTenants")}</Label>
              {selectedGyms.map((gymId) => {
                const gym = gyms.find((g) => g.id === gymId);
                return (
                  <Badge key={gymId} variant="secondary">
                    {gym?.name}
                    <X
                      className="h-3 w-3 ml-1 cursor-pointer"
                      onClick={() => toggleGym(gymId)}
                    />
                  </Badge>
                );
              })}
            </div>
          )}

          {/* Action Types */}
          <div className="space-y-2">
            <Label>{t("filters.actionTypes")}</Label>
            <div className="flex flex-wrap gap-2">
              {ACTION_TYPES.map((action) => (
                <Badge
                  key={action}
                  variant={selectedActions.includes(action) ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => toggleAction(action)}
                >
                  {ACTION_LABELS[action] || action}
                </Badge>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
